/**
 * The world outside the player.
 *
 * The brief asks for a clear distinction between VISIBLE NPCs and BACKGROUND
 * ECONOMIC AGENTS, and 20-50 agents is enough for V1. That distinction is
 * drawn here deliberately, because it is the thing that lets the neighbourhood
 * grow into a city later without re-architecting:
 *
 *   - `VisibleNpc`  : has a position, is rendered, walks around. Purely
 *                     presentational - it never touches money.
 *   - `EconomicAgent`: has no position and is never rendered. It is a household
 *                     with demand for services, and it is what actually shapes
 *                     the market the player sells into.
 *
 * At V1 the economic layer is intentionally light: it modulates how much work
 * is available, so the neighbourhood feels like it has a rhythm (busy spring
 * weekends, quiet weeks) rather than emitting leads at a constant rate.
 */
import type { GameState } from '../state/GameState.js';
import { Rng } from '../core/rng.js';
import { CUSTOMER_LOTS, LOCATIONS, WORLD_BOUNDS } from '../data/world.js';

export interface VisibleNpc {
  id: string;
  x: number;
  z: number;
  targetX: number;
  targetZ: number;
  speed: number;
  colour: number;
  /** Purely cosmetic variation so the crowd does not look cloned. */
  height: number;
  /**
   * What this person is doing. Not simulated economically - it only decides
   * where they walk - but it is the difference between a crowd and a random
   * walk, because their destinations then make sense for the hour.
   */
  routine: 'commuter' | 'shopper' | 'resident' | 'stroller';
  /** Seconds to stand still on arrival before choosing a new destination. */
  dwell: number;
  dwellRemaining: number;
  /** Where this person lives, so they can go home at night. */
  homeId: string;
}

export interface EconomicAgent {
  /** Which lot this household occupies, or null for a passer-by household. */
  locationId: string | null;
  /** How much they care about their yard, 0..1. Drives demand. */
  propertyPride: number;
  /** How much they can pay, 0..1. Drives payout variance. */
  affluence: number;
}

export class WorldSimulation {
  private npcs: VisibleNpc[] = [];
  private agents: EconomicAgent[] = [];
  /**
   * Its own RNG stream, derived from the game seed.
   *
   * This must NOT share the economic RNG. `initialise` runs both when a game
   * starts and when one is loaded, and if it drew from the shared stream a
   * loaded game would continue on a different sequence than one that was never
   * saved - which is exactly the divergence the save round-trip test caught.
   * A separate stream also enforces the rule that presentation can never
   * perturb the economy.
   */
  private rng: Rng;

  constructor(seed: number) {
    this.rng = new Rng((seed ^ 0x5eed1e) >>> 0);
  }

  /**
   * Called when a game starts or loads. Reseeding first makes it idempotent:
   * the same seed always yields the same neighbourhood, however many times it
   * is called.
   */
  initialise(seed: number, visibleCount = 28): void {
    this.rng = new Rng((seed ^ 0x5eed1e) >>> 0);
    this.npcs = [];
    const homes = [...CUSTOMER_LOTS.map((l) => l.id), 'home'];
    for (let i = 0; i < visibleCount; i++) {
      const x = this.rng.float(WORLD_BOUNDS.minX + 10, WORLD_BOUNDS.maxX - 10);
      const z = this.rng.float(WORLD_BOUNDS.minZ + 10, WORLD_BOUNDS.maxZ - 10);
      const roll = this.rng.next();
      const routine: VisibleNpc['routine'] =
        roll < 0.32 ? 'commuter' : roll < 0.58 ? 'shopper'
        : roll < 0.82 ? 'resident' : 'stroller';
      this.npcs.push({
        id: `npc_${i}`,
        x, z,
        targetX: x, targetZ: z,
        speed: this.rng.float(1.1, 2.0),
        colour: this.rng.pick([
          0x445a7a, 0x7a4455, 0x4a7a55, 0x7a6a44, 0x5a4a7a, 0x3f5f5f, 0x8a6f5a,
        ]),
        height: this.rng.float(1.62, 1.9),
        routine,
        dwell: this.rng.float(2, 14),
        dwellRemaining: 0,
        homeId: this.rng.pick(homes),
      });
      this.pickNewTarget(this.npcs[i]!, 12);
    }

    this.agents = CUSTOMER_LOTS.map((lot) => ({
      locationId: lot.id,
      propertyPride: this.rng.float(0.25, 1),
      affluence: lot.lotSize === 'large' ? this.rng.float(0.6, 1) : this.rng.float(0.2, 0.8),
    }));
    // A pool of households that are not on a visible lot: future demand for
    // when the neighbourhood expands, and market texture today.
    for (let i = 0; i < 18; i++) {
      this.agents.push({
        locationId: null,
        propertyPride: this.rng.float(0.1, 1),
        affluence: this.rng.float(0.1, 1),
      });
    }
  }

  getVisibleNpcs(): readonly VisibleNpc[] {
    return this.npcs;
  }

  getEconomicAgents(): readonly EconomicAgent[] {
    return this.agents;
  }

  /**
   * Demand multiplier for today, from the background agents plus a weekly
   * rhythm. Weekends are when people notice their yard.
   */
  demandMultiplier(state: GameState): number {
    if (this.agents.length === 0) return 1;
    const avgPride =
      this.agents.reduce((s, a) => s + a.propertyPride, 0) / this.agents.length;
    const weekend = state.time.dayOfWeek >= 5 ? 1.25 : 0.95;
    return (0.7 + avgPride * 0.6) * weekend;
  }

  /**
   * Move the visible crowd. Presentation only - never touches game state.
   *
   * @param hour in-game hour, so destinations suit the time of day
   */
  tickVisible(deltaSeconds: number, hour = 12): void {
    for (const npc of this.npcs) {
      if (npc.dwellRemaining > 0) {
        // Standing still outside a shop or a front door reads as a person with
        // somewhere to be, rather than a unit that never stops pathing.
        npc.dwellRemaining -= deltaSeconds;
        continue;
      }
      const dx = npc.targetX - npc.x;
      const dz = npc.targetZ - npc.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 1.5) {
        npc.dwellRemaining = npc.dwell;
        this.pickNewTarget(npc, hour);
        continue;
      }
      const step = Math.min(dist, npc.speed * deltaSeconds);
      npc.x += (dx / dist) * step;
      npc.z += (dz / dist) * step;
    }
  }

  /**
   * Choose somewhere to go that suits both this person and the hour.
   *
   * Commuters head for the depot in the morning and home in the evening;
   * shoppers use the store and the tool shop through the day; residents
   * potter between houses; strollers use the park. It is a handful of weighted
   * picks, not a planner - but it is the difference between a neighbourhood
   * and a field of random walkers.
   */
  private pickNewTarget(npc: VisibleNpc, hour: number): void {
    const evening = hour >= 17 || hour < 7;
    const morning = hour >= 7 && hour < 10;

    let pool: string[];
    if (evening) {
      // Everyone drifts home as it gets dark.
      pool = [npc.homeId, npc.homeId, 'park', 'store'];
    } else if (npc.routine === 'commuter') {
      pool = morning ? ['depot', 'depot', 'bank'] : ['depot', 'store', npc.homeId];
    } else if (npc.routine === 'shopper') {
      pool = ['store', 'toolshop', 'bank', 'store'];
    } else if (npc.routine === 'stroller') {
      pool = ['park', 'park', 'store', npc.homeId];
    } else {
      pool = [npc.homeId, 'park', 'store', ...CUSTOMER_LOTS.map((l) => l.id)];
    }

    const id = this.rng.pick(pool);
    const destination = LOCATIONS.find((l) => l.id === id) ?? this.rng.pick(LOCATIONS);
    npc.targetX = destination.x + destination.entryX + this.rng.float(-5, 5);
    npc.targetZ = destination.z + destination.entryZ + this.rng.float(-5, 5);
    npc.dwell = this.rng.float(2, 14);
  }
}
